import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import Badge from "../models/Badge";
import Certification from "../models/Certification";
import BadgeService from "../services/BadgeService";
import CertificationService from "../services/CertificationService";
import { QueryKeyFactory } from "../services/queryKeys";
import UserService from "../services/UserService";
import { unwrap } from "../utils/queryFn";
import { createTableQuery, refreshTableQuery, TABLE_QUERY_STALE_TIME } from "../utils/tableQuery";

export const certificationKeys = new QueryKeyFactory(CertificationService.path);
const badgeKeys = new QueryKeyFactory(BadgeService.path);
const userKeys = new QueryKeyFactory(UserService.path);

export type CertificationListFilters = {
  badgeExternalId?: string;
  search?: string;
  status?: string;
  level?: string;
};

export const useCertificationList = (filters: CertificationListFilters = {}) => {
  const queryClient = useQueryClient();
  const badgeExternalId = filters.badgeExternalId ?? "";
  const search = filters.search ?? "";
  const status = filters.status ?? "";
  const level = filters.level ?? "";
  const coreUserId = AuthService.getUser()?.externalId;

  const [forceUpdateOn, setForceUpdateOn] = useState<number>();
  const [isLevelOptionAvailable, setIsLevelOptionAvailable] = useState(false);
  const [actualBadge, setActualBadge] = useState<Badge>();

  const currentUserQuery = useQuery({
    queryKey: userKeys.detail(`core:${coreUserId ?? "anonymous"}`),
    queryFn: () => unwrap(UserService.findByCoreUserId(coreUserId!)),
    enabled: !!coreUserId,
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const badgeNamesQuery = useQuery({
    queryKey: badgeKeys.list({ scope: "names", active: false }),
    queryFn: () => unwrap(BadgeService.findAllBadgesNames(false)),
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const activeBadgeNamesQuery = useQuery({
    queryKey: badgeKeys.list({ scope: "names", active: true }),
    queryFn: () => unwrap(BadgeService.findAllBadgesNames(true)),
    staleTime: TABLE_QUERY_STALE_TIME,
  });

  const onDataFetch = useMemo(
    () =>
      createTableQuery<Certification>({
        queryKey: (page, pageSize, sortBy, sortDir) =>
          certificationKeys.list({ page, pageSize, sortBy, sortDir, badgeExternalId, search, status, level }),
        queryFn: (page, pageSize) => unwrap(CertificationService.findFiltered(page, pageSize, badgeExternalId, search, status, level)),
        client: queryClient,
      }),
    [queryClient, badgeExternalId, search, status, level],
  );

  const refreshTable = () => refreshTableQuery(certificationKeys.lists(), () => setForceUpdateOn(Date.now()), queryClient);

  const createMutation = useMutation({
    mutationFn: (certification: Certification) => unwrap(CertificationService.createCertification(certification)),
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("Certification created successfully");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (certification: Certification) => unwrap(CertificationService.delete(certification)),
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("Certification deleted successfully");
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ externalId, nextStatus }: { externalId: string; nextStatus: string }) =>
      unwrap(CertificationService.changeStatus(externalId, nextStatus)),
    onSuccess: async () => {
      await refreshTable();
      NotificationService.info("Certification status updated successfully");
    },
  });

  const handleOnChangeBadge = async (selected: AutoCompleteUIDTO | null) => {
    if (selected === null) {
      setIsLevelOptionAvailable(false);
      setActualBadge(undefined);
      return;
    }
    const badge = await queryClient.fetchQuery({
      queryKey: badgeKeys.detail(selected.externalId),
      queryFn: () => unwrap(BadgeService.getByExternalId(selected.externalId)),
      staleTime: TABLE_QUERY_STALE_TIME,
    });
    setIsLevelOptionAvailable(badge.level);
    setActualBadge(badge);
  };

  const resetBadgeSelection = () => {
    setIsLevelOptionAvailable(false);
    setActualBadge(undefined);
  };

  return {
    onDataFetch,
    forceUpdateOn,
    badgesNames: badgeNamesQuery.data ?? [],
    activeBadgesNames: activeBadgeNamesQuery.data ?? [],
    userExternalId: currentUserQuery.data?.externalId,
    isLevelOptionAvailable,
    actualBadge,
    handleOnChangeBadge,
    resetBadgeSelection,
    handleOnCreate: (certification: Certification) => createMutation.mutate(certification),
    handleOnDelete: (certification: Certification) => deleteMutation.mutate(certification),
    handleChangeStatus: (externalId: string, nextStatus: string) => statusMutation.mutate({ externalId, nextStatus }),
  };
};
